import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { api } from '../lib/api';
import {
  SERIENLEISTUNG_TURNUS,
  SERIENLEISTUNG_TURNUS_LABEL,
  SERIENLEISTUNG_TYPEN,
  SERIENLEISTUNG_TYP_LABEL,
  serienleistungenApi,
  type Serienleistung,
  type SerienleistungTurnus,
  type SerienleistungTyp,
} from '../lib/serienleistungen-api';
import { requireAccess } from '../lib/guards';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

export const Route = createFileRoute('/serienleistungen/$id')({
  beforeLoad: () => requireAccess('/api/v1/serienleistungen'),
  component: SerienleistungDetailPage,
});

interface SubunternehmerListItem {
  id: string;
  firmenname: string;
  ansprechpartner: string | null;
}

function SerienleistungDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  return <SerienleistungDetail id={id} />;
}

export function SerienleistungDetail({ id }: { id: string }): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const serieQ = useQuery({
    queryKey: ['serienleistungen', 'detail', id],
    queryFn: () => serienleistungenApi.getById(id),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['serienleistungen', 'detail', id] });
    void qc.invalidateQueries({ queryKey: ['serienleistungen', 'list'] });
  };

  const update = useMutation({
    mutationFn: (input: Parameters<typeof serienleistungenApi.update>[1]) =>
      serienleistungenApi.update(id, input),
    onSuccess: invalidate,
  });

  const serie = serieQ.data?.item;

  return (
    <div className="space-y-6">
      <PageHeader
        title={serie?.bezeichnung ?? 'Serienleistung'}
        description={
          serie
            ? `${SERIENLEISTUNG_TYP_LABEL[serie.leistungstyp]} · ${SERIENLEISTUNG_TURNUS_LABEL[serie.turnus]} · nächste Fälligkeit ${formatGermanDateFromTimestamp(
                serie.naechsteFaelligkeit,
              )}`
            : '…'
        }
        actions={
          <Button variant="ghost" onClick={() => void navigate({ to: '/serienleistungen' })}>
            ← Zurück
          </Button>
        }
      />

      {serieQ.error ? <ErrorBanner error={serieQ.error as Error} /> : null}
      {update.error ? <ErrorBanner error={update.error as Error} /> : null}

      {!serie ? (
        <div className="text-sm text-slate-500">Lädt…</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`rounded-md border px-2 py-1 text-xs font-medium ${
                serie.aktiv
                  ? 'border-emerald-200 bg-emerald-100 text-emerald-800'
                  : 'border-slate-200 bg-slate-100 text-slate-700'
              }`}
            >
              {serie.aktiv ? 'Aktiv' : 'Pausiert'}
            </span>
            <Button
              variant="ghost"
              onClick={() => update.mutate({ aktiv: !serie.aktiv })}
              disabled={update.isPending}
            >
              {update.isPending ? 'Speichere…' : serie.aktiv ? 'Pausieren' : 'Reaktivieren'}
            </Button>
          </div>

          <StammPanel serie={serie} />

          <EditPanel
            serie={serie}
            onSave={(input) => update.mutate(input)}
            pending={update.isPending}
          />
        </>
      )}
    </div>
  );
}

function StammPanel({ serie }: { serie: Serienleistung }): JSX.Element {
  const subsQ = useQuery({
    queryKey: ['subunternehmer', 'list'],
    queryFn: () => api<{ items: SubunternehmerListItem[] }>('/api/v1/subunternehmer'),
  });
  const sub = (subsQ.data?.items ?? []).find((s) => s.id === serie.subunternehmerId);

  return (
    <dl className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2">
      <Field label="Leistungstyp">{SERIENLEISTUNG_TYP_LABEL[serie.leistungstyp]}</Field>
      <Field label="Turnus">{SERIENLEISTUNG_TURNUS_LABEL[serie.turnus]}</Field>
      <Field label="Nächste Fälligkeit">
        {formatGermanDateFromTimestamp(serie.naechsteFaelligkeit)}
      </Field>
      <Field label="Kunde">
        <Link
          to="/kunden/$id"
          params={{ id: serie.kundeId }}
          className="text-brand-700 hover:underline"
        >
          Kunde öffnen
        </Link>
      </Field>
      <Field label="Objekt">
        <Link
          to="/objekte/$id"
          params={{ id: serie.objektId }}
          className="text-brand-700 hover:underline"
        >
          Objekt öffnen
        </Link>
      </Field>
      <Field label="Subunternehmer">
        {sub ? (
          <>
            <span className="font-medium">{sub.firmenname}</span>
            {sub.ansprechpartner ? (
              <span className="text-slate-500"> · {sub.ansprechpartner}</span>
            ) : null}
          </>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </Field>
      <Field label="Notizen" wide>
        {serie.notizen ?? <span className="text-slate-400">—</span>}
      </Field>
    </dl>
  );
}

function EditPanel({
  serie,
  onSave,
  pending,
}: {
  serie: Serienleistung;
  onSave: (input: {
    bezeichnung?: string;
    leistungstyp?: SerienleistungTyp;
    turnus?: SerienleistungTurnus;
    naechsteFaelligkeit?: string;
    subunternehmerId?: string;
    notizen?: string;
  }) => void;
  pending: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [bezeichnung, setBezeichnung] = useState(serie.bezeichnung);
  const [leistungstyp, setLeistungstyp] = useState<SerienleistungTyp>(serie.leistungstyp);
  const [turnus, setTurnus] = useState<SerienleistungTurnus>(serie.turnus);
  const [dueDate, setDueDate] = useState(serie.naechsteFaelligkeit.slice(0, 10));
  const [notizen, setNotizen] = useState(serie.notizen ?? '');
  const [subId, setSubId] = useState<string>(serie.subunternehmerId ?? '');

  const subsQ = useQuery({
    queryKey: ['subunternehmer', 'list'],
    queryFn: () => api<{ items: SubunternehmerListItem[] }>('/api/v1/subunternehmer'),
  });

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-medium text-slate-700">Stamm bearbeiten</div>
        <Button variant="ghost" onClick={() => setOpen((v) => !v)} disabled={pending}>
          {open ? 'Abbrechen' : 'Bearbeiten'}
        </Button>
      </div>
      {open ? (
        <div className="space-y-3 border-t border-slate-200 pt-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Bezeichnung</span>
            <Input
              value={bezeichnung}
              onChange={(e) => setBezeichnung(e.target.value)}
              maxLength={200}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Leistungstyp</span>
            <Select
              value={leistungstyp}
              onChange={(e) => setLeistungstyp(e.target.value as SerienleistungTyp)}
            >
              {SERIENLEISTUNG_TYPEN.map((t) => (
                <option key={t} value={t}>
                  {SERIENLEISTUNG_TYP_LABEL[t]}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Turnus</span>
            <Select
              value={turnus}
              onChange={(e) => setTurnus(e.target.value as SerienleistungTurnus)}
            >
              {SERIENLEISTUNG_TURNUS.map((t) => (
                <option key={t} value={t}>
                  {SERIENLEISTUNG_TURNUS_LABEL[t]}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Nächste Fälligkeit
            </span>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Subunternehmer (optional)
            </span>
            <Select value={subId} onChange={(e) => setSubId(e.target.value)}>
              <option value="">– Kein Subunternehmer –</option>
              {(subsQ.data?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.firmenname}
                  {s.ansprechpartner ? ` · ${s.ansprechpartner}` : ''}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Notizen</span>
            <textarea
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              rows={3}
              value={notizen}
              onChange={(e) => setNotizen(e.target.value)}
            />
          </label>
          <Button
            onClick={() => {
              const input: {
                bezeichnung?: string;
                leistungstyp?: SerienleistungTyp;
                turnus?: SerienleistungTurnus;
                naechsteFaelligkeit?: string;
                subunternehmerId?: string;
                notizen?: string;
              } = {};
              if (bezeichnung.trim() !== serie.bezeichnung) input.bezeichnung = bezeichnung.trim();
              if (leistungstyp !== serie.leistungstyp) input.leistungstyp = leistungstyp;
              if (turnus !== serie.turnus) input.turnus = turnus;
              if (dueDate !== serie.naechsteFaelligkeit.slice(0, 10)) {
                input.naechsteFaelligkeit = dueDate;
              }
              if (subId !== (serie.subunternehmerId ?? '') && subId.length > 0) {
                input.subunternehmerId = subId;
              }
              if (notizen.trim() !== (serie.notizen ?? '')) input.notizen = notizen.trim();
              if (Object.keys(input).length > 0) onSave(input);
              setOpen(false);
            }}
            disabled={pending}
          >
            {pending ? 'Speichere…' : 'Speichern'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}): JSX.Element {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">{children}</dd>
    </div>
  );
}
